import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import ListContainer from 'core/containers/ListContainer'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import React from 'react'
import CardWithStatusBar from './CardWithStatusBar'
import { formatTime, healthLableMap } from './helper'
import Progress from 'core/components/progress/Progress'
import { HealthStatusBadge } from './HealthStatusBadge'
import { ServiceType } from './constant'
import { useLocalNetworkAccessAllowed } from 'core/hooks/useLocalNetworkAccessAllowed'
import Badge from 'core/elements/badge'
import ActionRequiredModalTrigger from 'core/components/ActionRequiredModalTrigger'
import { checkImageLibraryRequiredActions } from 'app/plugins/openstack/components/images/checkImageLibraryRequiredActions'

const useStyles = makeStyles<Theme, { showWarningCount: boolean }>((theme) => ({
  row: {
    display: 'grid',
    gridTemplateColumns: '460px 120px auto',
    gridAutoFlow: 'column',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    padding: 16,
    background: theme.components.card.background,
    border: `1px solid ${theme.components.card.border}`,
    borderRadius: 4,
    position: 'relative',
  },
  headerContent: {
    width: '100%',
  },
  headerText: {
    display: 'grid',
    gap: '4px',
    minWidth: 380,
  },
  badgeContainer: {
    minWidth: 0,
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    rowGap: theme.spacing(0.5),
  },
  subContainer: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    cursor: 'pointer',
  },
  checkContainer: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  content: {
    display: 'grid',
  },
  leftBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    borderRadius: '4px 0 0 4px',
  },
  success: {
    background: `${colors.green[100]} !important`,
    color: `${colors.green[80]} !important`,
  },
  warning: {
    background: `${colors.yellow[100]} !important`,
    color: `${colors.yellow[800]} !important`,
  },
  error: {
    background: `${colors.red[100]} !important`,
    color: `${colors.pink[800]} !important`,
  },
  iconsContainer: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    cursor: 'pointer',
  },
  statusContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    cursor: 'pointer',
  },
  iconWithCount: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  iconContainer: {
    background: theme.palette.grey[100],
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
  },
  divider: {
    width: 1,
    height: 20,
    background: colors.grey[200],
  },
  iconWrapper: {
    fontSize: 24,
    color: colors.blue[400],
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
    height: 56,
    background: 'linear-gradient(to bottom, #f8fbff, #e3effb)',
    borderRadius: 12,
    border: `1px solid ${colors.blue[800]}`,
  },
  tableContainer: {
    paddingTop: 24,
  },
  toggleCaret: {
    cursor: 'pointer',
  },
  actionRequiredBadge: {
    cursor: 'pointer',
  },
}))

const ServiceStatusCard = ({
  iconName,
  headerName,
  headerSubtext,
  successCount,
  errorCount,
  warningCount = 0,
  lastChecked,
  nextCheck,
  showAdvancedOptions,
  setShowAdvancedOptions,
  data,
  columns,
  loading,
  emptyContent,
  statusHealth,
  statusName,
  serviceType,
  hostEndpoint,
}: {
  iconName: string
  headerName: string
  headerSubtext: string
  successCount: number
  errorCount: number
  warningCount?: number
  lastChecked: Date
  nextCheck: Date
  showAdvancedOptions: boolean
  setShowAdvancedOptions: (show: boolean) => void
  data: any[]
  columns: any[]
  loading: boolean
  emptyContent: React.ReactNode
  statusHealth: string
  statusName: string
  serviceType: ServiceType
  hostEndpoint?: string
}) => {
  const showWarningCount = serviceType === ServiceType.Image || serviceType === ServiceType.Compute
  const classes = useStyles({ showWarningCount })
  const isBrowserLocalNetworkAccessAllowed = useLocalNetworkAccessAllowed()

  // Actions which require user attention for Image service health issues
  const actionRequiredIssues = React.useMemo(() => {
    if (serviceType !== ServiceType.Image) {
      return []
    }

    const needToValidateCert = statusName === healthLableMap.CertificateError
    const glanceHostNotReachable = statusName === healthLableMap.NetworkError

    return checkImageLibraryRequiredActions({
      needToValidateCert,
      isBrowserLocalNetworkAccessAllowed,
      glanceHostNotReachable,
      hostEndpoint,
    })
  }, [serviceType, statusName, isBrowserLocalNetworkAccessAllowed, hostEndpoint])

  return (
    <CardWithStatusBar statusName={statusName}>
      <div className={classes.headerContent}>
        <div className={classes.row}>
          <div
            className={classes.iconsContainer}
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          >
            <div className={classes.logo}>
              <FontAwesomeIcon className={classes.iconWrapper}>{iconName}</FontAwesomeIcon>
            </div>

            <div className={classes.headerText}>
              <Text variant="subtitle2">{headerName}</Text>
              <Text variant="body2">{headerSubtext}</Text>
            </div>
          </div>
          <div className={classes.badgeContainer}>
            {loading ? (
              <Progress inline overlay={false} loading={loading} />
            ) : (
              <div className={classes.badgeRow}>
                {serviceType === ServiceType.Image && actionRequiredIssues.length ? (
                  <ActionRequiredModalTrigger
                    issues={actionRequiredIssues}
                    modalTitle="Needs Action"
                    modalIntroText="The following actions are required to access the Image Library Endpoint:"
                    trigger={({ openWithEvent }) => (
                      <Badge
                        className={classes.actionRequiredBadge}
                        variant="warning"
                        text={`Needs Action (${actionRequiredIssues.length})`}
                        onClick={openWithEvent}
                        ellipsisAt={50}
                      />
                    )}
                  />
                ) : (
                  <HealthStatusBadge
                    value={statusName}
                    serviceType={serviceType}
                    combinedServiceState={true}
                  />
                )}
              </div>
            )}
          </div>
          <div className={clsx(classes.divider, classes.subContainer)} />
          <div className={classes.statusContainer}>
            <div className={classes.iconWithCount}>
              <div className={clsx(classes.iconContainer, classes.success)}>
                <FontAwesomeIcon className={classes.icon}>circle-check</FontAwesomeIcon>
              </div>
              <Text variant="body2">{successCount}</Text>
            </div>
            <div className={classes.divider} />
            <div className={classes.iconWithCount}>
              <div className={clsx(classes.iconContainer, classes.error)}>
                <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
              </div>
              <Text variant="body2">{errorCount}</Text>
            </div>
            {showWarningCount ? (
              <>
                <div className={classes.divider} />
                <div className={classes.iconWithCount}>
                  <div className={clsx(classes.iconContainer, classes.warning)}>
                    <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
                  </div>
                  <Text variant="body2">{warningCount}</Text>
                </div>
              </>
            ) : null}
          </div>
          <div className={clsx(classes.divider, classes.subContainer)} />
          <div className={classes.checkContainer}>
            <div className={classes.iconContainer}>
              <FontAwesomeIcon className={classes.icon}>calendar-check</FontAwesomeIcon>
            </div>
            <div className={classes.content}>
              <Text variant="caption1">{formatTime(lastChecked)}</Text>
              <Text variant="body2">Last checked</Text>
            </div>
          </div>
          <div className={clsx(classes.divider, classes.subContainer)} />
          <div className={classes.checkContainer}>
            <div className={classes.iconContainer}>
              <FontAwesomeIcon className={classes.icon}>calendar-clock</FontAwesomeIcon>
            </div>
            <div className={classes.content}>
              <Text variant="caption1">{formatTime(nextCheck)}</Text>
              <Text variant="body2">Next check</Text>
            </div>
          </div>
          <div
            className={classes.content}
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          >
            <FontAwesomeIcon solid size="xs" className={classes.toggleCaret}>
              {showAdvancedOptions ? 'chevron-up' : 'chevron-down'}
            </FontAwesomeIcon>
          </div>
        </div>

        {showAdvancedOptions && (
          <div className={classes.tableContainer}>
            <ListContainer
              data={data}
              loading={loading}
              columns={columns}
              searchTargets={[]}
              uniqueIdentifier={'id'}
              disableToolbar
              emptyContent={emptyContent}
              showBreadcrumbs={false}
              hideCheckboxes
            />
          </div>
        )}
      </div>
    </CardWithStatusBar>
  )
}

export default ServiceStatusCard
